import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUp, Square, ArrowDown } from 'lucide-react';
import ApprovalCard from './ApprovalCard';
import ToolChip from './ToolChip';
import ToolGroup from './ToolGroup';
import { claudeChatAPI } from '../../services/api';
import useChatStream from './useChatStream';
import { formatText } from './formatText';
import { isNearBottom, currentActivity, SUGGESTIONS } from './chatHelpers';

const MAX_LINES = 6;

// Folds each run of consecutive tool items into one { kind: 'toolgroup' } entry.
function groupTools(items) {
  const out = [];
  for (const it of items) {
    const prev = out[out.length - 1];
    if (it.kind === 'tool' && prev?.kind === 'toolgroup') prev.tools.push(it);
    else if (it.kind === 'tool') out.push({ kind: 'toolgroup', key: `g${it.key}`, tools: [it] });
    else out.push(it);
  }
  return out;
}

export default function ChatView({ sessionId, onDisabled, mobile = false }) {
  const { items, running, connected, error, send, stop, decide } = useChatStream(sessionId, onDisabled);
  const [text, setText] = useState('');
  const [showPill, setShowPill] = useState(false);
  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const stick = useRef(true); // owner is at (or near) the bottom
  const sending = useRef(false);
  const [commands, setCommands] = useState([]);
  // Refresh the list each time a "/" starts a message: it fills in after the first run.
  const slash = /^\/(\S*)$/.exec(text);
  useEffect(() => {
    if (slash) claudeChatAPI.commands().then((r) => setCommands(r.data.data)).catch(() => {});
  }, [!!slash]);
  const matches = slash ? commands.filter((c) => c.toLowerCase().includes(slash[1].toLowerCase())).slice(0, 8) : [];

  const scrollDown = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    stick.current = true;
    setShowPill(false);
  }, []);

  const onScroll = () => {
    const near = isNearBottom(scrollRef.current);
    stick.current = near;
    if (near) setShowPill(false);
  };

  // New content: follow only when the owner is already near the bottom, else offer the pill.
  useLayoutEffect(() => {
    if (!items.length) return;
    if (stick.current) scrollDown();
    else setShowPill(true);
  }, [items, scrollDown]);

  // Auto-grow the textarea up to MAX_LINES.
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const cs = window.getComputedStyle(ta);
    const line = parseFloat(cs.lineHeight) || 22;
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    ta.style.height = `${Math.min(ta.scrollHeight, line * MAX_LINES + pad)}px`;
  }, [text]);

  useEffect(() => { stick.current = true; }, [sessionId]);

  const submitText = async (raw) => {
    const t = raw.trim();
    if (!t || sending.current) return false; // while Claude works the sidecar queues it
    sending.current = true;
    stick.current = true;
    const ok = await send(t);
    sending.current = false;
    return ok;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (await submitText(text)) setText(''); // a failed send keeps the draft
  };
  const canSend = !!text.trim();

  return (
    <div className="cc-chat">
      <div className="cc-scroll" ref={scrollRef} onScroll={onScroll}>
        <div className="cc-col">
          {items.length === 0 && (
            <div className="cc-empty">
              <h2 className="cc-empty-title">What should we work on?</h2>
              <div className="cc-chips">
                {SUGGESTIONS.map((s) => (
                  <button type="button" key={s} className="cc-chip" disabled={running} onClick={() => submitText(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {(mobile ? groupTools(items) : items).map((it) => {
            if (it.kind === 'toolgroup') return <ToolGroup key={it.key} tools={it.tools} />;
            if (it.kind === 'queued') return null; // shown below, after the live run
            if (it.kind === 'user') return <div key={it.key} className="cc-user"><div className="cc-user-bubble">{it.text}</div></div>;
            if (it.kind === 'text') return <div key={it.key} className="cc-claude">{formatText(it.text)}</div>;
            if (it.kind === 'tool') return <ToolChip key={it.key} item={it} />;
            if (it.kind === 'approval') return <ApprovalCard key={it.key} item={it} onDecide={decide} />;
            if (it.kind !== 'status') return null;
            return <div key={it.key} className={`cc-status${it.level === 'error' ? ' cc-status-error' : ''}`}>{it.text}</div>;
          })}
          {running && <div className="cc-typing" aria-label="Claude is working"><span /><span /><span /></div>}
          {items.filter((it) => it.kind === 'queued').map((it) => (
            <div key={it.key} className="cc-user cc-queued">
              <span className="cc-queued-label">Queued</span>
              <div className="cc-user-bubble">{it.text}</div>
            </div>
          ))}
        </div>
      </div>
      {showPill && (
        <button type="button" className="cc-pill" onClick={scrollDown}><ArrowDown size={14} /> New messages</button>
      )}
      <div className="cc-composer-wrap">
        <div className="cc-col">
          {running && <div className="cc-activity" role="status"><span className="cc-activity-dot" />{currentActivity(items) || 'Working…'}</div>}
          {!running && connected && items.length > 0 && (
            <div className="cc-quick">
              <button type="button" className="cc-chip" onClick={() => submitText('Continue from where you stopped.')}>Continue</button>
            </div>
          )}
          {!connected && <div className="cc-note">Reconnecting…</div>}
          {error && <div role="alert" className="cc-note cc-note-error">{error}</div>}
          {matches.length > 0 && (
            <div className="cc-slash" role="listbox" aria-label="Skills and commands">
              {matches.map((c) => (
                <button type="button" key={c} role="option" className="cc-slash-item" onClick={() => { setText(`/${c} `); taRef.current?.focus(); }}>/{c}</button>
              ))}
            </div>
          )}
          <form onSubmit={submit} className="cc-composer">
            <textarea
              ref={taRef} className="cc-input" rows={1} value={text}
              enterKeyHint="send" autoCapitalize="off" autoCorrect="off" spellCheck={false} aria-label="Message Claude Code"
              onChange={(e) => setText(e.target.value)}
              placeholder={running ? 'Queue a message for after this' : 'Message Claude Code'}
              // Enter sends on every screen size; Shift+Enter adds a new line (owner's choice).
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) submit(e); }}
            />
            {running && (
              <button type="button" className="cc-send cc-send-stop" onClick={stop} aria-label="Stop"><Square size={13} fill="currentColor" /></button>
            )}
            {(!running || canSend) && (
              <button type="submit" className="cc-send" aria-label={running ? 'Queue' : 'Send'} disabled={!canSend}><ArrowUp size={16} /></button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
